import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Theme,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import arrayMutators from "final-form-arrays";
import { pick } from "lodash";
import { useMemo } from "react";
import { FormProps, withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import { ISaveResourceLibraryRequest } from "services/KnowfullyAdminClient";
import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";

const useStyles = (theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
});

const { Form } = withTypes<ISaveResourceLibraryRequest>();

type ResourceLibraryFormProps = {
  resourceLibrary?: ISaveResourceLibraryRequest;
  submitLabel: string;
  onSubmit: (values: ISaveResourceLibraryRequest) => void;
  onCancel?: () => void;
};

const ResourceLibraryForm = ({
  resourceLibrary,
  submitLabel,
  onCancel,
  onSubmit,
}: ResourceLibraryFormProps & FormProps<ISaveResourceLibraryRequest>) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());

  const init = useMemo(
    () => ({
      ...pick(resourceLibrary ?? {}, [
        "name",
        "description",
        "libraryPath",
        "contentName",
        "organizationId",
      ]),
    }),
    [resourceLibrary]
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      mutators={{ ...arrayMutators }}
      render={({ handleSubmit, submitting, submitError, values, form }) => (
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <ContextOrganizationField
                values={values}
                formChange={form.change}
                mode={
                  resourceLibrary
                    ? ContextOrganizationFieldMode.EditManuallyTriggered
                    : ContextOrganizationFieldMode.SetByHeader
                }
              />

              <Field name="name" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "nameLabel" })}
                      InputLabelProps={{ required: true }}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="description">
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "descriptionLabel" })}
                      InputLabelProps={{ required: false }}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="libraryPath" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "resourceLibraryFormPathLabel" })}
                      InputLabelProps={{ required: true }}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="contentName">
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "resourceLibraryFormContentNameLabel" })}
                      InputLabelProps={{ required: false }}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button
              disabled={submitting}
              onClick={onCancel}
            >
              {t({ id: "cancelButton" })}
            </Button>

            <Button
              type="submit"
              color="primary"
              disabled={submitting}
            >
              {submitLabel}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default ResourceLibraryForm;
